import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Copy, Mail, Crown, ShieldCheck, AlertCircle, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const ROLE_BADGE = {
  owner: "bg-[#FF5722]/10 text-[#FF5722] border-[#FF5722]/30",
  admin: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30",
  member: "bg-[#F4F4EE] text-[#5C5C66] border-[#EAEAE3]",
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "member" });
  const [inviting, setInviting] = useState(false);

  const isOwner = user?.role === "owner";
  const canInvite = user?.role === "owner" || user?.role === "admin";

  const load = () => api.get("/team").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const r = await api.post("/team/invites", form);
      toast.success(`Invite created${user?.has_smtp ? " — email sent" : " — copy the link below"}`);
      setOpen(false);
      setForm({ email: "", role: "member" });
      // copy link
      navigator.clipboard?.writeText(r.data.invite_url).catch(() => {});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally { setInviting(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    await api.delete(`/team/invites/${id}`);
    toast.success("Invite revoked");
    load();
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/team/members/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeMember = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the workspace?`)) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!data) return <div className="p-10 text-[#5C5C66]">Loading…</div>;

  const seatsLeft = Math.max(0, data.seat_limit - data.seats_used);
  const atLimit = seatsLeft === 0;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">— Team</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Workspace members</h1>
          <p className="text-[#5C5C66] mt-2">Invite teammates to share campaigns, prospects and outreach.</p>
        </div>
        {canInvite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={atLimit} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white font-medium shadow-soft" data-testid="invite-member-btn">
                <Plus className="h-4 w-4 mr-2" /> Invite member
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md rounded-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Invite a teammate</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="label-mono">Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@yourdomain.com" className="mt-2 rounded-lg" data-testid="invite-email" />
                </div>
                <div>
                  <Label className="label-mono">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-2 rounded-lg" data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member · view & edit prospects, drafts, sends</SelectItem>
                      <SelectItem value="admin">Admin · everything except billing & ownership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!user?.has_smtp && (
                  <div className="flex items-start gap-2 text-xs text-[#5C5C66] bg-[#FAFAF7] border border-[#EAEAE3] rounded-lg p-3">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>Add SMTP credentials in <Link to="/app/settings" className="underline font-medium text-[#0A0A0F]">Settings</Link> to send invites by email automatically. We'll still generate a copyable link.</span>
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" disabled={inviting} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white" data-testid="invite-submit">
                    {inviting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : "Send invite"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <SeatTile label="Plan" value={data.plan} mono />
        <SeatTile label="Seat limit" value={data.seat_limit} />
        <SeatTile label="Seats used" value={`${data.seats_used} / ${data.seat_limit}`} accent={atLimit ? "warn" : "ok"} />
      </div>

      {atLimit && data.seat_limit < 10 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3 text-sm">
          <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-amber-900">Seat limit reached</div>
            <div className="text-amber-800">Upgrade to <Link to="/app/billing" className="underline font-medium">a higher plan</Link> to invite more teammates.</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-[#EAEAE3] font-display font-bold">Members</div>
        <div>
          {data.members.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-5 py-4 border-b border-[#EAEAE3] last:border-0" data-testid={`member-row-${m.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FA7] to-[#1E4FD6] text-white flex items-center justify-center text-xs font-bold shrink-0">
                  {m.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {m.name}
                    {m.is_owner && <Crown className="h-3.5 w-3.5 text-[#FF5722]" />}
                  </div>
                  <div className="text-xs text-[#5C5C66] truncate">{m.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isOwner && !m.is_owner ? (
                  <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                    <SelectTrigger className="h-8 rounded-lg w-28 text-xs bg-white" data-testid={`member-role-${m.id}`}>
                      <Badge className={`${ROLE_BADGE[m.role]} font-mono uppercase text-[10px] tracking-wider rounded-md border`} variant="outline">{m.role}</Badge>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">member</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`${ROLE_BADGE[m.role]} font-mono uppercase text-[10px] tracking-wider rounded-md border`} variant="outline">{m.role}</Badge>
                )}
                {isOwner && !m.is_owner && (
                  <button onClick={() => removeMember(m.id, m.name)} className="text-[#9A9AA3] hover:text-red-500 transition-colors" data-testid={`remove-member-${m.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.invites.length > 0 && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="px-5 py-4 border-b border-[#EAEAE3] font-display font-bold flex items-center gap-2">
            Pending invites <Badge variant="outline" className="rounded-md text-[10px] font-mono">{data.invites.length}</Badge>
          </div>
          <div>
            {data.invites.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between px-5 py-4 border-b border-[#EAEAE3] last:border-0" data-testid={`invite-row-${inv.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#F4F4EE] flex items-center justify-center text-[#5C5C66] shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{inv.email}</div>
                    <div className="text-xs text-[#5C5C66] truncate">Invited by {inv.invited_by} · expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${ROLE_BADGE[inv.role]} font-mono uppercase text-[10px] tracking-wider rounded-md border`} variant="outline">{inv.role}</Badge>
                  <Button onClick={() => copyLink(inv.invite_url)} variant="outline" size="sm" className="rounded-lg border-[#EAEAE3]" data-testid={`copy-invite-${inv.id}`}>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                  </Button>
                  {canInvite && (
                    <button onClick={() => revoke(inv.id)} className="text-[#9A9AA3] hover:text-red-500 transition-colors p-1" data-testid={`revoke-invite-${inv.id}`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SeatTile({ label, value, accent, mono }) {
  const color = accent === "warn" ? "text-[#F59E0B]" : accent === "ok" ? "text-[#16A34A]" : "text-[#0A0A0F]";
  return (
    <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft">
      <div className="label-mono mb-2">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tighter ${color} ${mono ? "uppercase font-mono text-2xl" : ""}`}>{value}</div>
    </div>
  );
}
